import { useTranslation } from 'next-i18next'
import { useEffect } from 'react'
import {
	useCurrentRefinements,
	useInstantSearch,
	useRange,
	useSearchBox,
} from 'react-instantsearch'

import useAiSearchStore from '@/stores/aiSearchStore'
import useSearchSelectorStore from '@/stores/searchSelectorStore'
import formatDuration from '@/utils/formatting/formatDuration'

import { SLIDER_LIMIT } from '../Filters/Duration/Duration.hooks'
import proFacets from '../Filters/ProFilter/ProFacets'
import StreamingServiceIcon from '../StreamingServiceIcon/StreamingServiceIcon'
import { UseRefinementsProps } from './CurrentRefinements.types'

const DURATION_RANGE = [-Infinity, Infinity]

export const useRefinements = (): UseRefinementsProps => {
	const { t } = useTranslation(['search'])

	const { items, refine } = useCurrentRefinements()

	const { setIndexUiState, indexUiState } = useInstantSearch()

	const { query: currentQuery, refine: refineSearchBox } = useSearchBox()

	const { searchByAudio, searchByPrompt, clearAiSearch } = useAiSearchStore()
	const clearInputDraft = useSearchSelectorStore(
		(state) => state.clearInputDraft
	)

	/**
	 * SEARCH BY AUDIO
	 */
	const searchByAudioRefinement = searchByAudio
		? [
				{
					label: (
						<>
							<StreamingServiceIcon service={searchByAudio.service} />
							{searchByAudio.title}
						</>
					),
					remove: () => clearAiSearch(),
				},
		  ]
		: []

	/**
	 * SEARCH BOX
	 */
	const clearSearchQuery = (): void => {
		clearInputDraft()
		refineSearchBox('')

		setIndexUiState((prevState) => ({
			...prevState,
			query: '',
		}))
	}

	const clearSearchByPrompt = (): void => {
		clearAiSearch()
		clearSearchQuery()
	}

	const searchBox =
		(currentQuery.length > 0 || searchByPrompt) && !searchByAudio
			? [
					{
						label: `${t('search-term')}: ${
							searchByPrompt ? searchByPrompt : currentQuery
						}`,
						remove: () =>
							searchByPrompt ? clearSearchByPrompt() : clearSearchQuery(),
						isSearchByPrompt: Boolean(searchByPrompt),
					},
			  ]
			: []

	/**
	 * DURATION
	 */
	const durationStart =
		indexUiState.range?.Duration?.split(':').map((value) =>
			parseInt(value, 10)
		) ?? DURATION_RANGE

	const durationRefined =
		SLIDER_LIMIT >= durationStart[1] || 0 < durationStart[0]

	const durationLabel = `${t('duration')}: ${formatDuration(durationStart[0])}
	${' - '}
	${
		durationStart[1] < SLIDER_LIMIT
			? formatDuration(durationStart[1])
			: `${formatDuration(SLIDER_LIMIT)}+`
	}`

	const duration = durationRefined
		? [
				{
					label: durationLabel,
					remove: () =>
						setIndexUiState((prevState) => ({
							...prevState,
							range: {
								...prevState.range,
								Duration: null,
							},
						})),
				},
		  ]
		: []

	/**
	 * BPM
	 */
	const {
		start: openingBpmStart,
		range: openingBpmRange,
		refine: refineOpeningBpm,
	} = useRange({
		attribute: 'TempoStart',
	})

	const {
		start: closingBpmStart,
		range: closingBpmRange,
		refine: refineClosingBpm,
	} = useRange({
		attribute: 'TempoFinish',
	})

	const openingBpmRefined =
		Number.isFinite(openingBpmStart[0]) || Number.isFinite(openingBpmStart[1])

	let openingBpmLabel: string

	if (openingBpmStart[0] <= 0 && Number.isFinite(openingBpmStart[1])) {
		openingBpmLabel = `${t('opening-bpm')}: <${openingBpmStart[1]}`
	} else if (openingBpmStart[0] > 0 && !Number.isFinite(openingBpmStart[1])) {
		openingBpmLabel = `${t('opening-bpm')}: >${openingBpmStart[0]}`
	} else if (openingBpmRefined) {
		openingBpmLabel = `${t('opening-bpm')}: ${openingBpmStart[0]} - ${
			openingBpmStart[1]
		}`
	}

	const openingBpm = openingBpmRefined
		? [
				{
					label: openingBpmLabel,
					remove: () =>
						refineOpeningBpm([openingBpmRange.min, openingBpmRange.max]),
				},
		  ]
		: []

	const closingBpmRefined =
		Number.isFinite(closingBpmStart[0]) || Number.isFinite(closingBpmStart[1])

	let closingBpmLabel: string

	if (closingBpmStart[0] <= 0 && Number.isFinite(closingBpmStart[1])) {
		closingBpmLabel = `${t('closing-bpm')}: <${closingBpmStart[1]}`
	} else if (closingBpmStart[0] > 0 && !Number.isFinite(closingBpmStart[1])) {
		closingBpmLabel = `${t('closing-bpm')}: >${closingBpmStart[0]}`
	} else if (closingBpmRefined) {
		closingBpmLabel = `${t('closing-bpm')}: ${closingBpmStart[0]} - ${
			closingBpmStart[1]
		}`
	}

	const closingBpm = closingBpmRefined
		? [
				{
					label: closingBpmLabel,
					remove: () =>
						refineClosingBpm([closingBpmRange.min, closingBpmRange.max]),
				},
		  ]
		: []

	/** PRO */
	const deselectedPros = items?.find((item) =>
		item.attribute.startsWith('Composers.CollectionSociety')
	)?.refinements

	const selectedPros = deselectedPros?.length
		? proFacets.filter(
				(pro) =>
					!deselectedPros?.some(
						(deselectedPro) =>
							deselectedPro.label.replace('-', '') === pro.value
					)
		  )
		: null

	const removePro = (pro: string): void => {
		const proList = [
			...indexUiState.refinementList['Composers.CollectionSociety'],
			`-${pro}`,
		]

		setIndexUiState((prevState) => ({
			...prevState,
			refinementList: {
				...prevState.refinementList,
				'Composers.CollectionSociety':
					proList.length !== proFacets.length ? proList : null,
			},
		}))
	}

	const pros = selectedPros
		? selectedPros.map((pro) => ({
				label: `${pro.value}`,
				remove: () => {
					removePro(pro.value)
				},
		  }))
		: []

	/** SIMPLE REFINEMENT LISTS AND OTHER CHECKBOXES/SWITCHES */
	const formatRefinements = (
		attribute: string,
		label: string
	): { label: string; remove: () => void }[] => {
		const refinements = items?.filter((item) =>
			item.attribute.startsWith(attribute)
		)[0]?.refinements

		if (!refinements) {
			return []
		}

		switch (attribute) {
			case 'Mix':
			case 'IsMajorKeyMood':
			case 'Vocal':
			case 'HasStems':
			case 'Explicit': {
				return refinements.map((refinement) => ({
					label,
					remove: () => refine(refinement),
				}))
			}
			case 'KeyStart':
			case 'KeyFinish':
			case 'Metre':
			case 'TempoName': {
				return refinements.map((refinement) => ({
					label: `${label}: ${refinement.value}`,
					remove: () => refine(refinement),
				}))
			}
			default:
				return refinements.map((refinement) => ({
					label: `${refinement.value}`,
					remove: () => refine(refinement),
				}))
		}
	}

	const refinements = [
		/**
		 * Assembled in the order it needs to appear in UI
		 */
		...searchByAudioRefinement,
		...searchBox,
		...formatRefinements('MusicalStyles', t('musical-styles')),
		...formatRefinements('Moods', t('moods')),
		...formatRefinements('Instrumentations', t('instrumentations')),
		...formatRefinements('ProductionGenres', t('production-genres')),
		...formatRefinements('Toolkit', t('toolkit')),
		...formatRefinements('Mix', t('toggle-mixes')),
		...formatRefinements('HasStems', t('toggle-stems')),
		...duration,
		...formatRefinements('TempoName', t('tempo')),
		...openingBpm,
		...closingBpm,
		...pros,
		...formatRefinements(
			'IsMajorKeyMood',
			indexUiState.menu?.IsMajorKeyMood === 'true'
				? t('key-mood-major')
				: t('key-mood-minor')
		),
		...formatRefinements('KeyStart', t('key-opening')),
		...formatRefinements('KeyFinish', t('key-closing')),
		...formatRefinements('Metre', t('metre')),
		...formatRefinements('Vocal', t('vocals-toggle')),
		...formatRefinements('Explicit', t('hide-explicit')),
	]

	/**
	 * CLEAR ALL
	 */
	const handleClearAll = (): void => {
		clearInputDraft()
		setIndexUiState((prevState) => ({
			...prevState,
			menu: null,
			query: '',
			range: null,
			refinementList: null,
			sortBy: null,
			toggle: null,
		}))
		clearAiSearch()
	}

	useEffect(() => {
		let resetButtons: NodeListOf<Element>

		if (typeof window !== 'undefined') {
			resetButtons = document.querySelectorAll('[data-reset-search]')
			resetButtons.forEach((resetButton) => {
				resetButton.addEventListener('click', handleClearAll)
			})
		}

		return () => {
			resetButtons.forEach((resetButton) => {
				resetButton.removeEventListener('click', handleClearAll)
			})
		}
	}, [refinements])

	return {
		refinements,
		handleClearAll,
	}
}
